import React from 'react'

import { CopyLinkInput } from '@components/common/CopyLinkInput'
import {
  ButtonClean,
  Colors,
  List,
  Pagination,
  Skeleton,
  Subsection,
  Table,
  Tooltip,
  useMediaQueryContext,
} from '@fourthwall/components'
import { QuestionMarkIcon } from '@fourthwall/icons'

import { getAffiliateDescription } from '../../../../modules/Recommendations/modules/ReferralRecommendation/utils/getAffiliateDescription'
import type { AffiliateReferral } from './Referrals.types'
import BlogThumbnail from './assets/blog-thumbnail.webp'
import { BlogLink } from './components/BlogLink/BlogLink'
import { ReferralListItem } from './components/RefferalsListItem/ReferralsListItem'

export type AffiliateReferralsViewProps = {
  page: number
  total: number
  totalPages: number
  limit: number
  setPage: (page: number) => void
  referrals: AffiliateReferral[]
  shortLink: string | undefined
  title: string
  description: React.ReactNode
}

export const ReferralsView = ({
  referrals,
  shortLink,
  page,
  total,
  limit,
  totalPages,
  setPage,
  title,
  description,
}: AffiliateReferralsViewProps) => {
  const { tablet } = useMediaQueryContext()

  const headers = [
    {
      label: 'Site',
      id: 'shopName',
      minWidth: 400,
      render: (row: AffiliateReferral) => {
        const { name, description } = getAffiliateDescription(row.affiliateTypeId)

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              color: row.isExpired ? Colors.GS600 : Colors.GS1000,
            }}
          >
            <ButtonClean label={row.shopName} href={`${row.shopDomain}`} target="_blank" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: Colors.GS600 }}>
              {name}
              <Tooltip reference={<QuestionMarkIcon />}>{description}</Tooltip>
            </div>
          </div>
        )
      },
    },
    {
      label: 'Started',
      id: 'fromTime',
      render: (row: AffiliateReferral) => (
        <div style={{ color: row.isExpired ? Colors.GS600 : Colors.GS1000 }}>
          {row.fromTime.toLocaleDateString()}
        </div>
      ),
    },
    {
      label: 'Ends',
      id: 'toTime',
      render: (row: AffiliateReferral) => (
        <div style={{ color: row.isExpired ? Colors.GS600 : Colors.GS1000 }}>
          {row.toTime.toLocaleDateString()}
        </div>
      ),
    },
  ]

  const data = referrals.map((referral) => ({
    ...referral,
    isExpired: new Date(referral.toTime) < new Date(),
    fromTime: new Date(referral.fromTime),
    toTime: new Date(referral.toTime),
  }))

  return (
    <>
      <Subsection title={title}>
        {shortLink ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>{description}</div>
            <CopyLinkInput href={shortLink} />
            <BlogLink
              title="Learn tips & tricks for referrals"
              description="A Guide to Fourthwall's Affiliate Programs: How to Qualify and Earn Rewards"
              href="https://fourthwall.com/referral-program"
              thumbnail={BlogThumbnail}
            />
          </div>
        ) : (
          <Skeleton>
            <Skeleton.Shape width={200} height={48} />
          </Skeleton>
        )}
      </Subsection>

      <Subsection title="Your referrals" borderless>
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {tablet ? (
            <Table
              headers={headers}
              data={data}
              emptyMessage="You don't have any referrals yet."
              minWidth={500}
            />
          ) : (
            <List
              data={data}
              renderItem={(item: AffiliateReferral) => <ReferralListItem item={item} />}
            />
          )}

          {totalPages > 1 && (
            <Pagination
              pageCount={totalPages}
              currentPage={page}
              total={{ count: total, limit }}
              onChange={setPage}
            />
          )}
        </div>
      </Subsection>
    </>
  )
}
